import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, Grid, IconButton, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { QuickTradeProto, QuoteType } from '../../../../proto/generated/quick-trade'
import { Broker_OrderType, TransactionType } from '../../../../proto/generated/order'
import StrategiesMenu from '../../../predefinedStrategies'
import { ExtraSmallButton, InteractiveTooltip, NonInteractiveTooltip } from '../../../custom'
import { createQuickTrade, deleteQuickTrade, modifyQuickTrade } from '../../../../services/apis/quickTrades'
import SecuritySearchModal from '../../../securitySearch/SecuritySearchModal'
import { LOGOS_BASE_URL } from '../../../../assets/constants'
import { getSecurityAsync, getSecurityKey } from '../../../../util/security'
import { useDispatch } from 'react-redux'
import { getColorForSymbol } from '../../../../util/general'
import { ExpressionCallbackData } from '../../../securitySearch/typings'
import { defaultQuickTrade } from '../../../../util/quicktradewidget'
import DraggableDialogTitle from '../../DraggableDialogTitle'
import { ClearIcon } from '@mui/x-date-pickers'
import { setOpenQuickTradeSettingsDialog } from '../../../../reducer/quickTrades.reducer'
import { useSelector } from 'react-redux'
import { selectQuickTrade, selectQuickTradeDialogMode, selectQuickTradeDialogOpen, selectQuickTradeEditTemplateId, selectQuickTrades } from '../../../../selectors/quickTrades'
import BinIcon from '../../../../assets/icons/BinIcon'
import Edit from '../../../../assets/icons/drawingTools/edit'
import ArrowBackIcon from '../../../../assets/icons/ArrowBackIcon'
import AddIcon from '../../../../assets/icons/AddIcon'
import { StrategyTemplateLegProto, StrategyTemplateProto } from '../../../../proto/generated/strategy'
import StrategyLegsRenderer from '../../../strategyTemplate/StrategyLegsRenderer'
import { SecurityCategory_CategoryCode } from '../../../../proto/generated/security'
import { selectPrimarySecurity } from '../../../../selectors/home'
import { Exchange } from '../../../../proto/generated/common'
import Info from '../../../../assets/icons/InfoIcon'
import QuickTradeTooltip from './QuickTradeTooltip'

type Props = {}

enum QuantityType {
    AMOUNT = 'Amount',
    MULTIPLIER = 'Multiplier',
    LOTS = 'Lots',
    QUANTITY = 'Quantity'
}

const AddQuickTrade = (props: Props) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const { primary } = useSelector(selectPrimarySecurity)
    const [securityAuto, setSecurityAuto] = useState(true)
    const [quickTrade, setQuickTrade] = useState<QuickTradeProto>(defaultQuickTrade())
    const open = useSelector(selectQuickTradeDialogOpen)
    const editTemplateId = useSelector(selectQuickTradeEditTemplateId)
    const quickTradeDialogMode = useSelector(selectQuickTradeDialogMode)
    const [quantityType, setQuantityType] = useState(QuantityType.MULTIPLIER)
    const quickTrades = useSelector(selectQuickTrades)
    const editQuickTrade = useSelector(selectQuickTrade(editTemplateId ?? ''))
    const [selectedTradeType, setSelectedTradeType] = useState<'buy' | 'sell' | 'strategy'>('strategy')
    const [nameError, setNameError] = useState('')
    const [templateError, setTemplateError] = useState('')
    const [mode, setMode] = useState<'add' | 'edit' | undefined>('add')

    useEffect(() => {
        if (quickTradeDialogMode === 'view') {
            setMode(undefined)
            setQuickTrade(defaultQuickTrade())
            setSecurityAuto(true)
        } else {
            if (editTemplateId) {
                const newTrade = editQuickTrade ? { ...editQuickTrade } : defaultQuickTrade()
                const newSecurityAuto = editQuickTrade.security?.code ? false : true
                if (newSecurityAuto) {
                    newTrade.security = {
                        code: primary?.code ?? 'NIFTY_50',
                        exchange: primary?.exchange ?? Exchange.NSE
                    }
                }
                setQuickTrade(newTrade)
                setSecurityAuto(newSecurityAuto)
                setQuantityType(editTemplateId && quickTrade.templateOrderExecutionParameters?.amount ? QuantityType.AMOUNT : quickTrade.templateOrderExecutionParameters?.quantity ? QuantityType.QUANTITY : quickTrade.templateOrderExecutionParameters?.lots ? QuantityType.LOTS : QuantityType.MULTIPLIER)
                setSelectedTradeType(editQuickTrade && editQuickTrade.templateOrderExecutionParameters?.transactionType ? (editQuickTrade.templateOrderExecutionParameters.transactionType === TransactionType.BUY ? 'buy' : 'sell') : 'strategy')
                setMode('edit')
            } else {
                setQuickTrade(defaultQuickTrade())
                setSecurityAuto(true)
                setMode('add')
            }
        }
    }, [editTemplateId, editQuickTrade, primary, quickTradeDialogMode])

    const [securityModalOpen, setSecuritiesModalOpen] = useState<boolean>(false)
    const [selectedSecurity, setSelectedSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const securityKey = useMemo(() => quickTrade.security ? getSecurityKey(quickTrade.security?.exchange ?? 0, quickTrade.security?.code ?? '') : '', [quickTrade.security?.code, quickTrade.security?.exchange])
    useEffect(() => { if (securityKey) getSecurityAsync(securityKey).then(setSelectedSecurity); else setSelectedSecurity(undefined) }, [securityKey])

    useEffect(() => {
        if (['buy', 'sell'].includes(selectedTradeType)) {
            if (securityAuto || [SecurityCategory_CategoryCode.OPTIONS, SecurityCategory_CategoryCode.FUTURES].includes(selectedSecurity?.categoryCode)) {
                setQuantityType(QuantityType.LOTS)
                handleMultiplierChange({ target: { value: 1 } } as any, QuantityType.LOTS)
            } else {
                setQuantityType(QuantityType.QUANTITY)
                handleMultiplierChange({ target: { value: 1 } } as any, QuantityType.QUANTITY)
            }
        } else if (quantityType === QuantityType.AMOUNT) {
            setQuantityType(QuantityType.AMOUNT)
            handleMultiplierChange({ target: { value: 10000 } } as any, QuantityType.AMOUNT)
        } else {
            setQuantityType(QuantityType.MULTIPLIER)
            handleMultiplierChange({ target: { value: 1 } } as any, QuantityType.MULTIPLIER)
        }
    }, [selectedSecurity, securityAuto, selectedTradeType])

    const showTradeOptions = useMemo(() => {
        if (!selectedSecurity || securityAuto) return ['strategy', 'buy', 'sell']
        if (selectedSecurity?.hasFo) {
            if (selectedSecurity?.categoryCode === SecurityCategory_CategoryCode.INDEX) {
                setSelectedTradeType('strategy')
                return ['strategy']
            }
            else return ['strategy', 'buy', 'sell']
        }
        setSelectedTradeType('buy')
        if (!selectedSecurity?.hasFo) return ['buy', 'sell']
        return ['buy', 'sell']
    }, [selectedSecurity, securityAuto])

    const showAmount = useMemo(() => {
        if (
            selectedTradeType === 'buy' ||
            (quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs?.length === 1 &&
                quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs?.[0].transactionType === TransactionType.BUY)
        ) return true
        return false
    }, [quickTrade, selectedTradeType])

    const showMultiplier = useMemo(() => {
        return selectedTradeType === 'strategy'
    }, [selectedTradeType])

    const showLots = useMemo(() => {
        return selectedTradeType !== 'strategy'
    }, [selectedTradeType])

    const showQuantity = useMemo(() => {
        return selectedTradeType !== 'strategy' && (!selectedSecurity || selectedSecurity?.categoryCode === SecurityCategory_CategoryCode.STOCKS)
    }, [selectedTradeType])

    useEffect(() => {
        if (selectedTradeType !== 'strategy') {
            setStrategyLegs([])
            setTemplateError('')
        }
    }, [selectedTradeType])

    useEffect(() => {
        if (!showAmount) {
            setQuantityType(QuantityType.MULTIPLIER)
            handleMultiplierChange({ target: { value: 10000 } } as any, QuantityType.MULTIPLIER)
        }
    }, [showAmount])

    useEffect(() => {
        if (open) {
            setNameError('')
            setQuickTrade(defaultQuickTrade())
            setSecurityAuto(true)
        }
    }, [open])

    const handleClose = useCallback(() => {
        dispatch(setOpenQuickTradeSettingsDialog({
            settingsDialogOpen: false,
            dialogMode: 'view',
            editTemplateId: undefined
        }))
    }, [dispatch])

    const onStrategySelected = (template: StrategyTemplateProto) => {
        setTemplateError('')
        setQuickTrade({
            ...quickTrade,
            templateOrderExecutionParameters: {
                ...quickTrade.templateOrderExecutionParameters,
                strategyLegs: template.strategyTemplateLegs
            },
            actionName: template.name,
            description: template.description
        })
    }

    const setStrategyLegs = (legs: StrategyTemplateLegProto[]) => {
        setQuickTrade(quickTrade => ({
            ...quickTrade,
            templateOrderExecutionParameters: {
                ...quickTrade.templateOrderExecutionParameters,
                strategyLegs: {
                    strategyTemplateLegs: legs
                }
            }
        }))
    }

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNameError('')
        setQuickTrade({ ...quickTrade, actionName: e.target.value })
    }

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuickTrade({ ...quickTrade, description: e.target.value })
    }

    const handleMultiplierChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, quantityType: QuantityType) => {
        const data = { ...quickTrade }
        if (quantityType === QuantityType.AMOUNT) {
            data.templateOrderExecutionParameters = {
                ...data.templateOrderExecutionParameters,
                multiplier: undefined,
                lots: undefined,
                quantity: undefined,
                amount: Number(e.target.value) > 0 ? Number(e.target.value) : 1
            } as any
        } else if (quantityType === QuantityType.MULTIPLIER) {
            data.templateOrderExecutionParameters = {
                ...quickTrade.templateOrderExecutionParameters,
                multiplier: Number(e.target.value) > 0 ? Number(e.target.value) : 1,
                amount: undefined,
                lots: undefined,
                quantity: undefined,
            } as any
        } else if (quantityType === QuantityType.LOTS) {
            data.templateOrderExecutionParameters = {
                ...quickTrade.templateOrderExecutionParameters,
                multiplier: undefined,
                amount: undefined,
                lots: Number(e.target.value) > 0 ? Number(e.target.value) : 1,
                quantity: undefined,
            } as any
        } else if (quantityType === QuantityType.QUANTITY) {
            data.templateOrderExecutionParameters = {
                ...quickTrade.templateOrderExecutionParameters,
                multiplier: undefined,
                amount: undefined,
                lots: undefined,
                quantity: Number(e.target.value) > 0 ? Number(e.target.value) : 1,
            } as any
        }
        setQuickTrade(data)
    }

    const handleQuoteValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value
        if (quickTrade.templateOrderExecutionParameters?.quoteType === QuoteType.LEVEL && (Number(value) > 5 || Number(value) < -5)) {
            if (Number(value) > 5) value = '5'
            else value = '-5'
        }
        setQuickTrade({
            ...quickTrade,
            templateOrderExecutionParameters: {
                ...quickTrade.templateOrderExecutionParameters,
                quoteValue: value
            } as any
        })
    }

    const handleQuoteValueBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Math.round(Number(e.target.value))
        if (quickTrade.templateOrderExecutionParameters?.quoteType === QuoteType.LEVEL && (value > 5 || value < -5)) {
            if (value > 5) value = 5
            else value = -5
        }
        setQuickTrade({
            ...quickTrade,
            templateOrderExecutionParameters: {
                ...quickTrade.templateOrderExecutionParameters,
                quoteValue: value
            } as any
        })
    }

    const handleSubmit = () => {
        if (selectedTradeType === 'strategy' && (!quickTrade.templateOrderExecutionParameters?.strategyLegs || quickTrade.templateOrderExecutionParameters.strategyLegs.strategyTemplateLegs.length === 0)) {
            setTemplateError('Please select a strategy template')
            return
        }
        if (!quickTrade.actionName) {
            setNameError('Please set action name')
            return
        }
        const newQuickTrade = {
            ...quickTrade
        }
        if (securityAuto) {
            newQuickTrade.security = undefined
        }
        if (selectedTradeType !== 'strategy') {
            newQuickTrade.templateOrderExecutionParameters = {
                ...newQuickTrade.templateOrderExecutionParameters,
                transactionType: selectedTradeType === 'buy' ? TransactionType.BUY : TransactionType.SELL,
                strategyLegs: undefined
            }
        } else {
            newQuickTrade.templateOrderExecutionParameters = {
                ...newQuickTrade.templateOrderExecutionParameters,
                transactionType: undefined
            }
        }
        if (mode === 'edit') {
            modifyQuickTrade(newQuickTrade)
        } else {
            createQuickTrade({ body: newQuickTrade })
        }
        handleClose()
        setQuickTrade(defaultQuickTrade())
        setSecurityAuto(true)
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{ sx: { minWidth: '800px' } }}
            sx={{ zIndex: 13000 }}
        >
            <DraggableDialogTitle id="alert-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1, pl: quickTradeDialogMode !== 'view' ? 1 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {
                        quickTradeDialogMode !== 'view' ?
                            <IconButton
                                aria-label="close"
                                onClick={() => {
                                    dispatch(setOpenQuickTradeSettingsDialog({
                                        settingsDialogOpen: true,
                                        dialogMode: 'view',
                                        editTemplateId: undefined
                                    }))
                                }}
                                sx={{ height: '34px' }}
                            >
                                <ArrowBackIcon fontSize={"small"} />
                            </IconButton>
                            : null
                    }
                    {
                        quickTradeDialogMode === 'view' ?
                            'Quick Trade Settings' :
                            mode === 'edit' ?
                                'Edit Quick Trade' :
                                'Add Quick Trade'
                    }
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        height: '34px'
                    }}
                >
                    <ClearIcon fontSize={"small"} />
                </IconButton>
            </DraggableDialogTitle>
            <DialogContent sx={quickTradeDialogMode === 'view' ? { p: 0 } : { pb: 0 }}>
                {
                    quickTradeDialogMode === 'view' ?
                        <Box sx={{ height: '100%' }}>
                            {
                                quickTrades.length === 0 ?
                                    null :
                                    <Table sx={{ tableLayout: "auto", width: "100%" }}>
                                        <TableHead sx={{ background: theme => theme.palette.background.default }}>
                                            <TableRow sx={{ height: '34px' }}>
                                                <TableCell sx={{ py: 0 }}>Action Name</TableCell>
                                                <TableCell sx={{ py: 0 }}>Template</TableCell>
                                                <TableCell sx={{ py: 0 }}>Description</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {
                                                quickTrades.map((q) => (
                                                    <TableRow
                                                        sx={{
                                                            height: '34px',
                                                            position: 'relative',
                                                            '&:hover': {
                                                                background: theme => theme.palette.action.hover
                                                            },
                                                            '&:hover button': {
                                                                visibility: 'visible !important',
                                                                background: theme => theme.palette.background.default
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={q.actionName}>{q.actionName}</TableCell>
                                                        <TableCell sx={{ py: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><QuickTradeTooltip quickTrade={q} dontShowStrategyLegs /></TableCell>
                                                        <TableCell sx={{ py: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={q.description ? q.description : '-'}>{q.description ? q.description : '-'}</TableCell>
                                                        <Stack direction="row" sx={{ position: 'sticky', right: 0, top: 0, height: '32px' }}>
                                                            <NonInteractiveTooltip arrow title="Delete" placement='top'>
                                                                <IconButton
                                                                    sx={{ width: '32px', height: '32px', visibility: 'hidden' }}
                                                                    onClick={() => {
                                                                        deleteQuickTrade(q)
                                                                    }}
                                                                >
                                                                    <BinIcon fontSize="small" />
                                                                </IconButton>
                                                            </NonInteractiveTooltip>
                                                            <NonInteractiveTooltip arrow title="Edit" placement='top'>
                                                                <IconButton
                                                                    sx={{ width: '32px', height: '32px', visibility: 'hidden' }}
                                                                    onClick={() => {
                                                                        dispatch(setOpenQuickTradeSettingsDialog({
                                                                            settingsDialogOpen: true,
                                                                            dialogMode: 'addOrEdit',
                                                                            editTemplateId: q.id
                                                                        }))
                                                                    }}
                                                                >
                                                                    <Edit />
                                                                </IconButton>
                                                            </NonInteractiveTooltip>
                                                        </Stack>
                                                    </TableRow>
                                                ))
                                            }
                                        </TableBody>
                                    </Table>
                            }
                            <ExtraSmallButton variant="outlined" sx={{ pr: 1, ml: 2, position: 'sticky', left: '16px', mt: quickTrades.length > 0 ? 1 : 0 }} onClick={() => {
                                dispatch(setOpenQuickTradeSettingsDialog({
                                    settingsDialogOpen: true,
                                    dialogMode: 'addOrEdit',
                                    editTemplateId: undefined
                                }))
                            }}>
                                <AddIcon />
                                {' Add new action'}
                            </ExtraSmallButton>
                        </Box> :
                        <Grid container direction={'column'} rowSpacing={1}>
                            <Grid container item spacing={2}>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Stack direction={'row'} sx={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" sx={{ display: 'flex' }}>
                                            Symbol
                                            <InteractiveTooltip title="Note*" placement='top' arrow>
                                                <IconButton size="small" sx={{ ml: '2px', p: 0 }}>
                                                    <Info sx={{ fontSize: '14px' }} />
                                                </IconButton>
                                            </InteractiveTooltip>
                                        </Typography>
                                    </Stack>
                                    <Button
                                        disableRipple
                                        color="inherit"
                                        variant='outlined'
                                        onClick={() => {
                                            setSecuritiesModalOpen(true)
                                        }}
                                        sx={{
                                            borderRadius: '4px',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            '& .MuiTypography-root': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            },
                                            minWidth: 'max-content !important',
                                            width: '100%',
                                            px: 1,
                                        }}
                                        data-event={'Search Clicked'}
                                        data-event-source={'stockbar'}
                                    >
                                        <Stack direction={'row'} spacing={1} sx={{ width: '100%' }} alignItems={'center'} justifyContent={'space-between'}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {securityAuto ? "Pick From Chart" :
                                                    selectedSecurity?.logoUrl ? (
                                                        <Avatar alt={selectedSecurity?.displayName} src={`${LOGOS_BASE_URL}${selectedSecurity?.logoUrl}.svg`} sx={{ width: 20, height: 20 }} />
                                                    ) : (
                                                        <Avatar
                                                            alt={selectedSecurity?.displayName}
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                fontSize: 10,
                                                                color: 'white',
                                                                bgcolor: getColorForSymbol(selectedSecurity?.symbol || selectedSecurity?.code || '', theme.palette.background.default)
                                                            }}
                                                        >
                                                            {(selectedSecurity?.symbol || selectedSecurity?.code || '')
                                                                .split(' ')
                                                                .map((word) => word[0]?.toUpperCase())
                                                                .slice(0, 2)
                                                                .join('')}
                                                        </Avatar>
                                                    )}
                                                {
                                                    securityAuto ? null :
                                                        <Typography noWrap variant="body1" maxWidth={'120px'} sx={{ fontWeight: 500 }}>
                                                            {selectedSecurity?.symbol}
                                                        </Typography>
                                                }
                                            </span>
                                            {
                                                securityAuto ? null :
                                                    <IconButton
                                                        aria-label="close"
                                                        onClick={(e) => {
                                                            setSecurityAuto(true)
                                                            e.stopPropagation()
                                                        }}
                                                        sx={{
                                                            height: '34px'
                                                        }}
                                                    >
                                                        <ClearIcon fontSize={"small"} />
                                                    </IconButton>
                                            }
                                        </Stack>
                                    </Button>
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption">Trade Type</Typography>
                                    <ToggleButtonGroup value={selectedTradeType} onChange={(e, value) => {
                                        if (value !== null) {
                                            setSelectedTradeType(value)
                                        }
                                    }} exclusive sx={{ height: '34px', width: '100%' }}>
                                        <ToggleButton sx={{ flex: 1 }} value='buy' disabled={!showTradeOptions.includes('buy')}>Buy</ToggleButton>
                                        <ToggleButton sx={{ flex: 1 }} value='sell' disabled={!showTradeOptions.includes('sell')}>Sell</ToggleButton>
                                        <ToggleButton sx={{ flex: 1 }} value='strategy' disabled={!showTradeOptions.includes('strategy')}>Strategy</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption">Template</Typography>
                                    <StrategiesMenu
                                        onStrategyClicked={onStrategySelected}
                                        showAddTemplate
                                        disabled={selectedTradeType !== 'strategy'}
                                        sxProps={{
                                            width: '100%'
                                        }}
                                        error={Boolean(templateError)}
                                        errorMessage={templateError}
                                    />
                                </Grid>
                                {(securityModalOpen) && (
                                    <SecuritySearchModal
                                        open={securityModalOpen}
                                        deleteIcon={true}
                                        enableExpressions={false}
                                        onCloseClicked={() => {
                                            setSecuritiesModalOpen(false)
                                        }}
                                        onSecuritySelected={(data: ExpressionCallbackData) => {
                                            const [security] = data.securities
                                            setQuickTrade({
                                                ...quickTrade,
                                                security: {
                                                    code: security?.code,
                                                    exchange: security?.exchange
                                                }
                                            })
                                            setSecurityAuto(false)
                                            setSecuritiesModalOpen(false)
                                        }}
                                    />
                                )}
                            </Grid>
                            {
                                (selectedTradeType !== 'strategy') ? null :
                                    <Grid container item direction={'column'} gap={2}>
                                        <StrategyLegsRenderer
                                            strategyLegs={quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs ?? []}
                                            setStrategyLegs={setStrategyLegs as any}
                                        />
                                    </Grid>
                            }
                            <Grid container item spacing={2}>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption">Order Type</Typography>
                                    <Select value={quickTrade.templateOrderExecutionParameters?.brokerOrderType} MenuProps={{ sx: { zIndex: 13000 } }} sx={{ width: '100%', height: '34px' }} onChange={(e) => {
                                        const newData = {
                                            ...quickTrade, templateOrderExecutionParameters: {
                                                ...quickTrade.templateOrderExecutionParameters,
                                                brokerOrderType: e.target.value as Broker_OrderType
                                            }
                                        }
                                        if (e.target.value === Broker_OrderType.MARKET_ORDER) {
                                            delete newData.templateOrderExecutionParameters.quoteType
                                            delete newData.templateOrderExecutionParameters.quoteValue
                                        } else {
                                            newData.templateOrderExecutionParameters.quoteType = QuoteType.BIPS
                                            newData.templateOrderExecutionParameters.quoteValue = 1
                                        }
                                        setQuickTrade(newData)
                                    }}>
                                        <MenuItem value={Broker_OrderType.MARKET_ORDER}>Market Order</MenuItem>
                                        <MenuItem value={Broker_OrderType.LIMIT_ORDER}>Limit Order</MenuItem>
                                    </Select>
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption" sx={{ display: 'flex' }}>Buffer
                                        <InteractiveTooltip title="Note*" placement='top' arrow>
                                            <IconButton size="small" sx={{ ml: '2px', p: 0 }}>
                                                <Info sx={{ fontSize: '14px' }} />
                                            </IconButton>
                                        </InteractiveTooltip>
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex',
                                    }}>
                                        <Select
                                            disabled={quickTrade.templateOrderExecutionParameters?.brokerOrderType !== Broker_OrderType.LIMIT_ORDER}
                                            value={quickTrade.templateOrderExecutionParameters?.quoteType ?? QuoteType.BIPS}
                                            MenuProps={{
                                                sx: {
                                                    zIndex: 13000
                                                }
                                            }}
                                            sx={{
                                                width: '112px',
                                                height: '34px',
                                                marginRight: '-1px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderTopRightRadius: 0,
                                                    borderBottomRightRadius: 0
                                                }
                                            }}
                                            onChange={(e) => {
                                                let quoteValue = quickTrade.templateOrderExecutionParameters?.quoteValue ?? 1
                                                if ((e.target.value as QuoteType) === QuoteType.LEVEL) {
                                                    if (quickTrade.templateOrderExecutionParameters?.quoteValue === undefined || quickTrade.templateOrderExecutionParameters?.quoteValue > 5) {
                                                        quoteValue = 5
                                                    } else if (quickTrade.templateOrderExecutionParameters?.quoteValue < -5) {
                                                        quoteValue = -5
                                                    }
                                                }
                                                setQuickTrade({
                                                    ...quickTrade,
                                                    templateOrderExecutionParameters: {
                                                        ...quickTrade.templateOrderExecutionParameters,
                                                        quoteType: e.target.value as QuoteType,
                                                        quoteValue
                                                    } as any
                                                })
                                            }}
                                        >
                                            <MenuItem value={QuoteType.BIPS}>BIPS</MenuItem>
                                            <MenuItem value={QuoteType.TICKS}>Ticks</MenuItem>
                                            <MenuItem value={QuoteType.LEVEL}>Level</MenuItem>
                                        </Select>
                                        <TextField
                                            value={quickTrade.templateOrderExecutionParameters?.quoteValue ?? ''}
                                            type='number'
                                            disabled={quickTrade.templateOrderExecutionParameters?.brokerOrderType !== Broker_OrderType.LIMIT_ORDER}
                                            onChange={handleQuoteValueChange}
                                            onBlur={(e)=>handleQuoteValueBlur(e as any)}
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderTopLeftRadius: 0,
                                                    borderBottomLeftRadius: 0
                                                },
                                                flex: '1',
                                                '& .MuiInputBase-root': {
                                                    height: '34px'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption">Quantity Type</Typography>
                                    <Box sx={{ m: '0 !important', display: 'flex' }}>
                                        <Select
                                            value={quantityType}
                                            MenuProps={{
                                                sx: {
                                                    zIndex: 13000
                                                }
                                            }}
                                            sx={{
                                                width: '112px',
                                                height: '34px',
                                                marginRight: '-1px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderTopRightRadius: 0,
                                                    borderBottomRightRadius: 0
                                                }
                                            }}
                                            onChange={(e) => {
                                                setQuantityType(e.target.value as QuantityType)
                                                handleMultiplierChange({ target: { value: 1 } } as any, e.target.value as QuantityType)
                                            }}
                                        >
                                            {showMultiplier ? <MenuItem value={QuantityType.MULTIPLIER}>{QuantityType.MULTIPLIER}</MenuItem> : null}
                                            {showAmount ? <MenuItem value={QuantityType.AMOUNT}>{QuantityType.AMOUNT}</MenuItem> : null}
                                            {showLots ? <MenuItem value={QuantityType.LOTS}>{QuantityType.LOTS}</MenuItem> : null}
                                            {showQuantity ? <MenuItem value={QuantityType.QUANTITY}>{QuantityType.QUANTITY}</MenuItem> : null}
                                        </Select>
                                        <TextField
                                            value={
                                                quantityType === QuantityType.MULTIPLIER ?
                                                    quickTrade.templateOrderExecutionParameters?.multiplier :
                                                    quantityType === QuantityType.AMOUNT ?
                                                        quickTrade.templateOrderExecutionParameters?.amount :
                                                        quantityType === QuantityType.LOTS ?
                                                            quickTrade.templateOrderExecutionParameters?.lots :
                                                            quickTrade.templateOrderExecutionParameters?.quantity
                                            }
                                            type='number'
                                            onChange={(e) => handleMultiplierChange(e, quantityType)}
                                            sx={{
                                                flex: 1,
                                                mt: '0 !important',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderTopLeftRadius: 0,
                                                    borderBottomLeftRadius: 0
                                                },
                                                '& .MuiInputBase-root': {
                                                    height: '34px'
                                                }
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid container item spacing={2}>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={4}>
                                    <Typography variant="caption">Action Name*</Typography>
                                    <TextField
                                        sx={{
                                            '& .MuiFormHelperText-root': {
                                                margin: 0
                                            },
                                            '& .MuiInputBase-root': {
                                                height: '34px'
                                            },
                                            width: '100%',
                                            marginBottom: nameError ? 0 : '20px',
                                        }}
                                        error={!!nameError}
                                        helperText={nameError ? nameError : undefined}
                                        value={quickTrade.actionName}
                                        onChange={handleNameChange}
                                    />
                                </Grid>
                                <Grid item container direction={'column'} alignItems={'flex-start'} xs={8}>
                                    <Typography variant="caption">Description</Typography>
                                    <TextField fullWidth sx={{
                                        '& .MuiInputBase-root': {
                                            height: '34px'
                                        }
                                    }} value={quickTrade.description} onChange={handleDescriptionChange} />
                                </Grid>
                            </Grid>
                        </Grid>
                }
            </DialogContent>
            <DialogActions sx={{ pt: 0 }}>
                {
                    quickTradeDialogMode === 'view' ? null :
                        <>
                            <ExtraSmallButton variant="outlined" onClick={() => {
                                setQuickTrade(defaultQuickTrade())
                                setSecurityAuto(true)
                                handleClose()
                            }}>
                                Cancel
                            </ExtraSmallButton>
                            <ExtraSmallButton variant="contained" sx={{ width: mode === 'edit' ? '70px' : '50px' }} onClick={handleSubmit}>
                                {
                                    mode === 'edit' ? 'Update' : 'Add'
                                }
                            </ExtraSmallButton>
                        </>
                }
            </DialogActions>
        </Dialog>
    )
}

export default AddQuickTrade